import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { usePageAnimation } from '../lib/usePageAnimation';
import { useApp } from '../context';
import { TrendingUp, TrendingDown, Minus, Star, Activity as ActivityIcon } from 'lucide-react';
import { cn, timeAgo as formatTimeAgo, formatInteractionType } from '../lib/utils';
import { motion } from 'motion/react';
import { activityApi, ApiActivity } from '../lib/api';
import { getCached, setCached } from '../lib/offlineStore';
import { registerCacheCleanup, hasChanged } from '../lib/moduleCache';
import ContactAvatar from './ContactAvatar';
import MiniLineChart from './MiniLineChart';
import ImageLightbox from './ImageLightbox';

const RING_COLOR = {
  call: 'ring-emerald-100',
  text: 'ring-blue-100',
  in_person: 'ring-violet-100',
  video: 'ring-amber-100',
};

// Module-level cache so re-mounts don't flash loading states
let _activityCache: ApiActivity | null = null;
registerCacheCleanup(() => {
  _activityCache = null;
});

export default function Activity() {
  const { state } = useApp();
  const shouldAnimate = usePageAnimation();
  const [timeframe, setTimeframe] = useState<'7' | '30'>('7');
  const [apiData, setApiData] = useState<ApiActivity | null>(_activityCache);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  // Fetch activity data from API for authenticated users
  useEffect(() => {
    if (state.isGuestMode) return;

    // Load from IDB cache first (skip if module cache exists)
    if (!_activityCache) {
      getCached<ApiActivity>('activity').then((cached) => {
        if (cached) {
          setApiData(cached.data);
          _activityCache = cached.data;
        }
      });
    }

    // Then fetch fresh data from API — only update state if data changed
    activityApi
      .get({ limit: 20 })
      .then((data) => {
        if (hasChanged(_activityCache, data)) setApiData(data);
        _activityCache = data;
        setCached('activity', data);
      })
      .catch(() => {});
  }, [state.isGuestMode]);

  // Chart data: API trends > local computation
  const emptyWeekly = [
    { name: 'Mon', value: 0 },
    { name: 'Tue', value: 0 },
    { name: 'Wed', value: 0 },
    { name: 'Thu', value: 0 },
    { name: 'Fri', value: 0 },
    { name: 'Sat', value: 0 },
    { name: 'Sun', value: 0 },
  ];
  const emptyMonthly = [
    { name: 'W1', value: 0 },
    { name: 'W2', value: 0 },
    { name: 'W3', value: 0 },
    { name: 'W4', value: 0 },
  ];

  const data = (() => {
    if (apiData) {
      return timeframe === '7' ? apiData.weeklyTrends : apiData.monthlyTrends;
    }
    return timeframe === '7' ? emptyWeekly : emptyMonthly;
  })();

  // Compute interaction counts
  const now = new Date();
  const periodDays = timeframe === '7' ? 7 : 30;
  const periodStart = new Date(now.getTime() - periodDays * 24 * 60 * 60 * 1000);
  const prevPeriodStart = new Date(periodStart.getTime() - periodDays * 24 * 60 * 60 * 1000);

  const currentPeriodCount = state.interactions.filter(
    (i) => new Date(i.date) >= periodStart,
  ).length;
  const prevPeriodCount = state.interactions.filter((i) => {
    const d = new Date(i.date);
    return d >= prevPeriodStart && d < periodStart;
  }).length;
  const trendPercent =
    prevPeriodCount > 0
      ? Math.round(((currentPeriodCount - prevPeriodCount) / prevPeriodCount) * 100)
      : 0;

  const displayCount = apiData
    ? timeframe === '7'
      ? apiData.weeklyTotal
      : apiData.monthlyTotal
    : currentPeriodCount;

  // Only show trend after API data loads (avoids stale local "+0%" flash)
  const rawTrend = apiData
    ? timeframe === '7'
      ? apiData.weeklyChangePct
      : apiData.monthlyChangePct
    : state.isGuestMode
      ? trendPercent
      : null;
  const displayTrend = typeof rawTrend === 'number' && isFinite(rawTrend) ? rawTrend : null;

  return (
    <div className="bg-[#f8f5f5] min-h-screen pb-32">
      {/* Header */}
      <header className="px-5 py-4 flex items-center">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Recent Activity</h1>
      </header>

      <div className="px-5 space-y-5">
        {/* Health Trend Card */}
        <motion.div
          initial={shouldAnimate ? { opacity: 0, y: 20 } : false}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-900 rounded-[2.5rem] p-7 text-white shadow-2xl relative overflow-hidden"
        >
          <div className="relative z-10">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-2">
                <div className="bg-amber-400 p-1.5 rounded-lg text-slate-900">
                  <Star size={16} fill="currentColor" />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                  Interaction Trend
                </span>
              </div>
              <div className="flex bg-white/10 p-1 rounded-xl">
                <button
                  onClick={() => setTimeframe('7')}
                  className={cn(
                    'px-3 py-1 rounded-lg text-[10px] font-bold transition-all',
                    timeframe === '7' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400',
                  )}
                >
                  7
                </button>
                <button
                  onClick={() => setTimeframe('30')}
                  className={cn(
                    'px-3 py-1 rounded-lg text-[10px] font-bold transition-all',
                    timeframe === '30' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400',
                  )}
                >
                  30
                </button>
              </div>
            </div>

            <div className="flex items-baseline gap-2 mb-6">
              <span className="text-4xl font-bold text-white">{displayCount}</span>
              <div
                className={cn(
                  'flex items-center font-bold text-xs',
                  displayTrend === null || displayTrend === 0
                    ? 'text-slate-400'
                    : displayTrend > 0
                      ? 'text-emerald-400'
                      : 'text-red-400',
                )}
              >
                {displayTrend !== null ? (
                  displayTrend === 0 ? (
                    <Minus size={14} className="mr-1" />
                  ) : displayTrend > 0 ? (
                    <>
                      <TrendingUp size={14} className="mr-1" />+{displayTrend}%{' '}
                    </>
                  ) : (
                    <>
                      <TrendingDown size={14} className="mr-1" />
                      {displayTrend}%{' '}
                    </>
                  )
                ) : (
                  <Minus size={14} className="mr-1" />
                )}
                <span className="text-slate-500 font-normal ml-1">Interactions</span>
              </div>
            </div>

            <div className="h-32 w-full">
              {!apiData && !state.isGuestMode ? (
                <div className="h-full w-full flex items-center justify-center">
                  <div className="w-5 h-5 rounded-full border-2 border-[#fa8585] border-t-transparent animate-spin"></div>
                </div>
              ) : (
                <MiniLineChart key={timeframe} data={data} />
              )}
            </div>
          </div>
          {/* Decorative Background Element */}
          <div className="absolute -right-8 -bottom-8 size-32 bg-[#fa8585]/10 rounded-full blur-3xl"></div>
        </motion.div>

        {/* Recent Interactions Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between px-2">
            <h2 className="text-xl font-bold text-slate-900">Recent Interactions</h2>
          </div>

          {state.interactions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center bg-white rounded-4xl border border-slate-100 shadow-sm mt-4">
              <ActivityIcon size={48} className="text-[#fa8585]/30 mb-4" />
              <h3 className="text-lg font-bold text-slate-900 mb-2">No activity yet</h3>
              <p className="text-slate-500 text-sm max-w-62.5 mx-auto">
                Log an interaction to start tracking your relationship activity.
              </p>
              <Link
                to="/log"
                className="mt-6 bg-[#fa8585] text-white px-6 py-2.5 rounded-2xl font-bold text-sm hover:scale-105 transition-transform shadow-md shadow-[#fa8585]/20"
              >
                Log Interaction
              </Link>
            </div>
          ) : (
            <div className="space-y-2 mt-4">
              {[...state.interactions]
                .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                .slice(0, 6)
                .map((interaction, idx) => {
                  const contact = state.contacts.find((c) => c.id === interaction.contactId);
                  if (!contact) return null;

                  const iType = interaction.type as keyof typeof RING_COLOR;
                  const timeAgoLabel = formatTimeAgo(interaction.date);

                  return (
                    <motion.div
                      key={interaction.id}
                      initial={shouldAnimate ? { opacity: 0, x: -20 } : false}
                      animate={{ opacity: 1, x: 0 }}
                      transition={shouldAnimate ? { delay: idx * 0.05 } : undefined}
                      className="bg-white rounded-2xl p-3.5 flex items-start gap-3 shadow-sm border border-slate-100"
                    >
                      <div
                        className={`size-10 rounded-xl overflow-hidden ring-2 shrink-0 ${RING_COLOR[iType] || 'ring-slate-100'}`}
                      >
                        <ContactAvatar
                          photoUrl={contact.photoUrl}
                          name={contact.name}
                          size="sm"
                          className="size-full! rounded-xl!"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">
                            {formatInteractionType(interaction.type)}
                          </p>
                          <span className="text-[9px] font-bold text-slate-300 uppercase">
                            {timeAgoLabel}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 leading-snug">
                          <Link
                            to={`/contact/${contact.id}`}
                            className="font-bold text-slate-900 hover:text-[#fa8585] transition-colors"
                          >
                            {contact.name.split(' ')[0]}
                          </Link>
                          {interaction.notes ? ` — ${interaction.notes}` : ''}
                        </p>
                        {interaction.attachments && interaction.attachments.length > 0 && (
                          <div className="flex gap-1.5 mt-1.5">
                            {interaction.attachments.map((url: string, idx: number) => (
                              <button
                                key={idx}
                                onClick={() => setLightboxUrl(url)}
                                className="h-16 max-w-24 rounded-lg overflow-hidden border border-slate-100"
                              >
                                <img src={url} alt="" className="h-full w-auto object-contain" />
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
            </div>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxUrl && <ImageLightbox src={lightboxUrl} onClose={() => setLightboxUrl(null)} />}
    </div>
  );
}
